import React, { useEffect, useMemo, useState } from 'react';
import cccApi, { RetencaoMarcacao } from '../api/cccApi';
import { isValidMarcacaoId } from '../config/prospeccaoMarcacao';
import ComercialManagement from '@crm/components/Comercial/ComercialManagement';
import '@crm/components/Comercial/ComercialManagement.css';

type MarcacaoSeed = {
  id?: number;
  marcacao_id?: string;
  nome_empresa?: string;
  nome_responsavel?: string;
  morada?: string;
  contacto_cliente?: string;
  email?: string;
  form_data?: Record<string, unknown>;
  [key: string]: unknown;
};

type Props = {
  marcacao: RetencaoMarcacao;
  onClose: () => void;
  onSuccess: () => void;
};

const swallowOverlayBackdropPointer = (e: React.MouseEvent) => {
  if (e.target === e.currentTarget) {
    e.stopPropagation();
  }
};

const CccSubmeterContratoRetencaoOverlay: React.FC<Props> = ({ marcacao, onClose, onSuccess }) => {
  const [seed, setSeed] = useState<Record<string, MarcacaoSeed> | null>(null);
  const marcacaoId = useMemo(() => {
    const raw = marcacao.marcacao_id || (marcacao.id != null ? String(marcacao.id) : '');
    return isValidMarcacaoId(raw) ? raw.trim() : '';
  }, [marcacao]);

  const embeddedContractApi = useMemo(
    () => ({
      uploadFile: async (formData: FormData) => {
        try {
          return await cccApi.uploadRetencaoContractFile(marcacaoId, formData);
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { message?: string; error?: string } } };
          const msg =
            ax.response?.data?.message ||
            ax.response?.data?.error ||
            (err instanceof Error ? err.message : 'Falha ao enviar ficheiro');
          return { success: false, error: msg, message: msg };
        }
      },
      persist: async (contractData: Record<string, unknown>) => {
        try {
          const res = await cccApi.submitRetencaoContract(marcacaoId, contractData);
          if (!res.success) {
            throw new Error(res.message || 'Erro ao salvar dados do contrato');
          }
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { message?: string } } };
          throw new Error(
            ax.response?.data?.message ||
              (err instanceof Error ? err.message : 'Erro ao salvar dados do contrato')
          );
        }
      },
    }),
    [marcacaoId]
  );

  useEffect(() => {
    if (!marcacaoId) return;
    let cancelled = false;

    const fallbackSeed: Record<string, MarcacaoSeed> = {
      [marcacaoId]: {
        ...marcacao,
        marcacao_id: marcacaoId,
        empresa: marcacao.nome_empresa,
        responsavel: marcacao.nome_responsavel,
        contacto: marcacao.contacto_cliente,
        form_data: marcacao.form_data,
      } as MarcacaoSeed,
    };

    const normalizeSeedEntry = (raw: MarcacaoSeed): MarcacaoSeed => ({
      ...raw,
      marcacao_id: raw.marcacao_id || marcacaoId,
      empresa: raw.nome_empresa || (raw as { empresa?: string }).empresa || marcacao.nome_empresa,
      responsavel:
        raw.nome_responsavel ||
        (raw as { responsavel?: string }).responsavel ||
        marcacao.nome_responsavel,
    });

    (async () => {
      try {
        const data = await cccApi.getRetencaoMarcacao(marcacaoId);
        if (!cancelled && data?.success && data.marcacao) {
          setSeed({
            [marcacaoId]: normalizeSeedEntry(data.marcacao as MarcacaoSeed),
          });
          return;
        }
      } catch {
        /* fallback to board payload */
      }
      if (!cancelled) {
        setSeed(fallbackSeed);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [marcacao, marcacaoId]);

  if (!marcacaoId) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">
          <p>Marcação inválida — não foi possível abrir Submeter Contrato.</p>
          <button type="button" className="rec-btn rec-btn-ghost" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    );
  }

  if (!seed) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">A carregar formulário…</div>
      </div>
    );
  }

  return (
    <div
      className="rec-solicitar-proposta-overlay"
      role="presentation"
      onMouseDown={swallowOverlayBackdropPointer}
    >
      <ComercialManagement
        embeddedContractOnly
        embeddedContractMarcacaoId={marcacaoId}
        embeddedContractApi={embeddedContractApi}
        embeddedMarcacaoSeed={seed as Record<string, never>}
        onEmbeddedContractClose={onClose}
        onEmbeddedContractSuccess={onSuccess}
      />
    </div>
  );
};

export default CccSubmeterContratoRetencaoOverlay;
